import React from "react";
import SvgIcon from "../../../parts/SvgIcon";
import ToolTip from "../../../parts/tooltip";
import { useRillContext } from "../../../rill/RillReact/share/base";
import CoachMultiSelect from "./CoachMultiSelect";
import CreateClassSelect from "./CreateClassSelect";

const WEEK_DAYS = [
  { key: "mon", label: "Monday", short: "Mon", dow: 0 },
  { key: "tue", label: "Tuesday", short: "Tue", dow: 1 },
  { key: "wed", label: "Wednesday", short: "Wed", dow: 2 },
  { key: "thu", label: "Thursday", short: "Thu", dow: 3 },
  { key: "fri", label: "Friday", short: "Fri", dow: 4 },
  { key: "sat", label: "Saturday", short: "Sat", dow: 5 },
  { key: "sun", label: "Sunday", short: "Sun", dow: 6 },
];

const DOW_TO_KEY = WEEK_DAYS.reduce((acc, d) => {
  acc[d.dow] = d.key;
  return acc;
}, {});

const DURATION_OPTIONS = [15, 30, 45, 60];

const MANUAL_LOCATION = "__manual__";

function gymAddressOptions(user) {
  const gyms = user?.pressCustomBranding?.gymLocations;
  if (!Array.isArray(gyms)) return [];

  const options = [];
  for (const gym of gyms) {
    const address = String(gym?.gymLocation ?? "").trim();
    if (!address) continue;
    const gymName = String(gym?.gymName ?? "").trim();
    const value = gymName ? `${gymName} - ${address}` : address;
    if (!options.includes(value)) options.push(value);
  }
  return options;
}

let rowSeq = 0;
function newRow(start = "09:00", end = "12:00", coachId = null) {
  rowSeq += 1;
  return { id: `tw-${rowSeq}`, start, end, coachId };
}

function emptyDayRanges() {
  return WEEK_DAYS.reduce((acc, d) => {
    acc[d.key] = [];
    return acc;
  }, {});
}

/** `HH:mm` / `HH:mm:ss` → `HH:mm` for <input type="time">. */
function hhmm(value) {
  if (typeof value !== "string") return "";
  const m = value.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  return `${String(Math.min(23, Number(m[1]))).padStart(2, "0")}:${m[2]}`;
}

function toMinutes(value) {
  const t = hhmm(value);
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

const LAST_MINUTE_OF_DAY = 23 * 60 + 59;

/** Minutes since midnight → `HH:mm`. */
function fromMinutes(total) {
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(
    total % 60
  ).padStart(2, "0")}`;
}

/** Map API `windows[]` onto the per-day editor state. */
function windowsToDayRanges(windows) {
  const out = emptyDayRanges();
  if (!Array.isArray(windows)) return out;
  for (const w of windows) {
    const key = DOW_TO_KEY[w.day_of_week ?? w.dayOfWeek];
    if (!key) continue;
    out[key].push(
      newRow(
        hhmm(w.start_time ?? w.startTime),
        hhmm(w.end_time ?? w.endTime),
        w.coachId != null ? String(w.coachId) : null
      )
    );
  }
  return out;
}

/**
 * Editor state → API payload. Mirrors the server's own rules so the gym gets a
 * useful message instead of a 400: each range must fit at least one slot, and
 * ranges on the same day may not overlap (which is also why two coaches cannot
 * share an hour on one consult - that needs a second consult).
 * @returns {{ windows?: Array<object>, error?: string }}
 */
function dayRangesToWindows(dayRanges, durationMinutes) {
  const windows = [];
  for (const day of WEEK_DAYS) {
    const rows = dayRanges[day.key] ?? [];
    const spans = [];
    for (const row of rows) {
      const s = toMinutes(row.start);
      const e = toMinutes(row.end);
      if (s == null || e == null) {
        return { error: `${day.label}: enter a start and end time.` };
      }
      if (e <= s) {
        return { error: `${day.label}: the end time must be after the start.` };
      }
      if (e - s < durationMinutes) {
        return {
          error: `${day.label}: the range must be at least ${durationMinutes} minutes to fit one consult.`,
        };
      }
      spans.push({ s, e });
      windows.push({
        day_of_week: day.dow,
        start_time: hhmm(row.start),
        end_time: hhmm(row.end),
        coachId: row.coachId || null,
      });
    }
    spans.sort((a, b) => a.s - b.s);
    for (let i = 1; i < spans.length; i += 1) {
      if (spans[i].s < spans[i - 1].e) {
        return {
          error: `${day.label}: two time ranges overlap. Consults run one at a time, so ranges on the same day must not overlap.`,
        };
      }
    }
  }
  if (windows.length === 0) {
    return { error: "Add availability on at least one day." };
  }
  return { windows };
}

const CreateTourModal = ({ isOpen, onClose, onSaved, editTour = null }) => {
  const { locals } = useRillContext();
  const editId = editTour ? String(editTour._id ?? editTour.id ?? "") : "";
  const isEdit = Boolean(editId);

  const gymAddresses = React.useMemo(
    () => gymAddressOptions(locals?.activeUserToRequest ?? locals?.activeUser),
    [locals?.activeUserToRequest, locals?.activeUser]
  );

  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [location, setLocation] = React.useState("");
  const [manualLocation, setManualLocation] = React.useState(false);
  const [locationType, setLocationType] = React.useState("physical");
  const [meetingProvider, setMeetingProvider] = React.useState("zoom");
  const [meetingStatus, setMeetingStatus] = React.useState(null);
  const [duration, setDuration] = React.useState(30);
  const [capacity, setCapacity] = React.useState("1");
  const [dayRanges, setDayRanges] = React.useState(emptyDayRanges);
  const [error, setError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  // Re-seed only when the modal opens on a different record, so typing is never
  // clobbered by a parent re-render handing back a new object with the same data.
  const seededFor = React.useRef(null);
  React.useEffect(() => {
    if (!isOpen) {
      seededFor.current = null;
      return;
    }
    const key = editId || "new";
    if (seededFor.current === key) return;
    seededFor.current = key;
    setError(null);
    if (editTour) {
      setName(editTour.name ?? "");
      setDescription(editTour.description ?? "");
      const savedLocation = editTour.location ?? "";
      setLocation(savedLocation);
      setManualLocation(
        Boolean(savedLocation) && !gymAddresses.includes(savedLocation)
      );
      setLocationType(
        editTour.location_type === "online" ? "online" : "physical"
      );
      setMeetingProvider(editTour.meeting_provider || "zoom");
      setDuration(Number(editTour.duration_minutes) || 30);
      setCapacity(
        editTour.capacity == null ? "unlimited" : String(editTour.capacity)
      );
      setDayRanges(windowsToDayRanges(editTour.windows));
    } else {
      setName("");
      setDescription("");
      setLocation("");
      setManualLocation(false);
      setLocationType("physical");
      setMeetingProvider("zoom");
      setDuration(30);
      setCapacity("1");
      setDayRanges(emptyDayRanges());
    }
  }, [isOpen, editId, editTour, gymAddresses]);

  // Connection status drives the inline "Connect" prompt for online consults.
  React.useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/actions/integrations/meeting/status", {
          credentials: "same-origin",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setMeetingStatus(data);
      } catch {
        /* best-effort; the server still enforces the connection on save */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleLocationChange = (value) => {
    if (value === MANUAL_LOCATION) {
      setManualLocation(true);
      if (gymAddresses.includes(location)) setLocation("");
      return;
    }
    setManualLocation(false);
    setLocation(value);
  };

  const mutateDay = (key, fn) =>
    setDayRanges((prev) => ({ ...prev, [key]: fn(prev[key] ?? []) }));

  const toggleDay = (key) =>
    mutateDay(key, (rows) => (rows.length > 0 ? [] : [newRow()]));

  /** Start where the previous range ended, so the added row is valid by default. */
  const addRange = (key) =>
    mutateDay(key, (rows) => {
      const lastEnd = toMinutes(rows[rows.length - 1]?.end);
      const span = Number(duration);
      if (lastEnd == null || lastEnd + span > LAST_MINUTE_OF_DAY) {
        return [...rows, newRow()];
      }
      return [
        ...rows,
        newRow(fromMinutes(lastEnd), fromMinutes(lastEnd + span)),
      ];
    });

  const removeRange = (key, id) =>
    mutateDay(key, (rows) => rows.filter((r) => r.id !== id));

  const patchRange = (key, id, patch) =>
    mutateDay(key, (rows) =>
      rows.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );

  const handleSave = async () => {
    const { windows, error: winError } = dayRangesToWindows(
      dayRanges,
      Number(duration)
    );
    if (winError) {
      setError(winError);
      return;
    }
    if (!name.trim()) {
      setError("Give the consult a name.");
      return;
    }
    if (locationType === "online" && !meetingProvider) {
      setError("Choose a meeting provider for the online consult.");
      return;
    }
    if (
      locationType === "online" &&
      meetingStatus &&
      !meetingStatus[meetingProvider]?.connected
    ) {
      setError(
        `Connect your ${
          meetingProvider === "google_meet" ? "Google Meet" : "Zoom"
        } account in Settings → Integrations first.`
      );
      return;
    }
    // Online consults are one-to-one; otherwise mirror resolveTourCapacity on
    // the server so a typo is caught here.
    const capacityValue =
      locationType === "online"
        ? 1
        : capacity === "unlimited"
          ? null
          : Number.parseInt(capacity, 10);
    if (capacityValue !== null && !(capacityValue >= 1)) {
      setError("Guests per slot must be a whole number of 1 or more.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        location: locationType === "physical" ? location.trim() : "",
        location_type: locationType,
        meeting_provider: locationType === "online" ? meetingProvider : null,
        duration_minutes: Number(duration),
        capacity: capacityValue,
        windows,
      };
      const res = await fetch(
        isEdit
          ? `/actions/scheduling/tours/${encodeURIComponent(editId)}`
          : "/actions/scheduling/tours",
        {
          method: isEdit ? "PUT" : "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data.error ?? data.message ?? "Failed to save the consult"
        );
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e?.message ?? "Failed to save the consult");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const unassignedCount = WEEK_DAYS.reduce(
    (n, d) => n + (dayRanges[d.key] ?? []).filter((r) => !r.coachId).length,
    0
  );

  const onlineNotConnected =
    locationType === "online" &&
    Boolean(meetingStatus) &&
    !meetingStatus[meetingProvider]?.connected;

  return (
    <div
      className="Modal-Box create-class-modal create-tour-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-tour-modal-title"
    >
      <button
        type="button"
        className="overlay"
        onClick={onClose}
        aria-label="Close modal"
      />
      <div className="box">
        <header>
          <h2 id="create-tour-modal-title">
            {isEdit ? "Edit Consult" : "Create Consult"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="close"
            data-button-type="tertiary"
            data-icon-only="true"
            aria-label="Close"
          >
            <img src="/img/new-icons/red-cross.svg" alt="" />
          </button>
        </header>

        <div className="body">
          <div className="create-class-modal-cols">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">
                Consult details
              </h3>
              <p className="create-class-modal-section-desc">
                Guests see this on your public booking page - no account needed.
              </p>
            </div>

            <div className="create-class-modal-form create-tour-modal-form">
              <div className="form-group">
                <label htmlFor="tour-name">Consult name</label>
                <input
                  id="tour-name"
                  type="text"
                  value={name}
                  maxLength={120}
                  placeholder="e.g. Intro Consult"
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="tour-description">Description</label>
                <textarea
                  id="tour-description"
                  rows={3}
                  value={description}
                  placeholder="What the guest can expect on the consult."
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="create-class-modal-form-two-cols">
                <div className="form-group">
                  <label
                    htmlFor="tour-duration"
                    className="create-tour-field-label"
                  >
                    Duration
                  </label>
                  <CreateClassSelect
                    id="tour-duration"
                    ariaLabel="Duration"
                    value={String(duration)}
                    onChange={(value) => setDuration(Number(value))}
                    options={DURATION_OPTIONS.map((m) => ({
                      value: String(m),
                      label: `${m} minutes`,
                    }))}
                  />
                </div>
                <div className="form-group">
                  <label
                    htmlFor="tour-capacity"
                    className="create-tour-field-label"
                  >
                    Guests per slot
                    {locationType === "online" && (
                      <ToolTip
                        id="tour-capacity-tip"
                        hintClassNames="tooltip-solid"
                        body="Online consults are one-to-one - each booking generates its own meeting link, so the guest count is locked to 1."
                      >
                        <SvgIcon
                          className="cursor-pointer"
                          src="/img/new-icons/question-circle.svg"
                          alt="Why guests per slot is locked"
                        />
                      </ToolTip>
                    )}
                  </label>
                  <input
                    id="tour-capacity"
                    type="number"
                    min={1}
                    value={
                      locationType === "online"
                        ? "1"
                        : capacity === "unlimited"
                          ? ""
                          : capacity
                    }
                    disabled={locationType === "online"}
                    placeholder={capacity === "unlimited" ? "Unlimited" : "1"}
                    onChange={(e) => setCapacity(e.target.value)}
                  />
                </div>
              </div>

              <div className="create-class-modal-form-two-cols">
                <div className="form-group">
                  <label
                    htmlFor="tour-location-physical"
                    className="create-tour-field-label"
                  >
                    Location
                  </label>
                  <fieldset className="create-tour-location-modes">
                    <legend className="sr-only">Consult location type</legend>
                    <button
                      id="tour-location-physical"
                      type="button"
                      data-button-type={
                        locationType === "physical" ? "primary" : "outline"
                      }
                      aria-pressed={locationType === "physical"}
                      onClick={() => setLocationType("physical")}
                    >
                      In person
                    </button>
                    <button
                      type="button"
                      data-button-type={
                        locationType === "online" ? "primary" : "outline"
                      }
                      aria-pressed={locationType === "online"}
                      onClick={() => setLocationType("online")}
                    >
                      Online
                    </button>
                  </fieldset>
                </div>

                {locationType === "online" ? (
                  <div className="form-group">
                    <label
                      htmlFor="tour-meeting-provider"
                      className="create-tour-field-label"
                    >
                      Meeting provider
                      <ToolTip
                        id="tour-meeting-provider-tip"
                        hintClassNames="tooltip-solid"
                        body="A unique meeting link is created for each booking and sent to the guest with their confirmation."
                      >
                        <SvgIcon
                          className="cursor-pointer"
                          src="/img/new-icons/question-circle.svg"
                          alt="About meeting links"
                        />
                      </ToolTip>
                    </label>
                    <CreateClassSelect
                      id="tour-meeting-provider"
                      ariaLabel="Meeting provider"
                      value={meetingProvider}
                      onChange={setMeetingProvider}
                      placeholder="Select a provider"
                      options={[
                        { value: "zoom", label: "Zoom" },
                        { value: "google_meet", label: "Google Meet" },
                      ]}
                    />
                  </div>
                ) : (
                  <div className="form-group">
                    <label
                      htmlFor={
                        gymAddresses.length > 0
                          ? "tour-location-select"
                          : "tour-location"
                      }
                      className="create-tour-field-label"
                    >
                      Address
                    </label>
                    {gymAddresses.length > 0 && (
                      <CreateClassSelect
                        id="tour-location-select"
                        ariaLabel="Address"
                        value={manualLocation ? MANUAL_LOCATION : location}
                        onChange={handleLocationChange}
                        placeholder="Select an address"
                        options={[
                          ...gymAddresses.map((address) => ({
                            value: address,
                            label: address,
                          })),
                          {
                            value: MANUAL_LOCATION,
                            label: "Enter a different address",
                          },
                        ]}
                      />
                    )}
                    {(manualLocation || gymAddresses.length === 0) && (
                      <input
                        id="tour-location"
                        type="text"
                        value={location}
                        placeholder="Where the guest should arrive"
                        onChange={(e) => setLocation(e.target.value)}
                      />
                    )}
                  </div>
                )}
              </div>

              {locationType === "online" &&
                meetingStatus &&
                !meetingStatus[meetingProvider]?.connected && (
                  <p className="create-class-modal-error" role="alert">
                    Your{" "}
                    {meetingProvider === "google_meet" ? "Google Meet" : "Zoom"}{" "}
                    account isn't connected.{" "}
                    <a href="/coaching-portal/settings/integrations">
                      Connect it in Settings → Integrations
                    </a>{" "}
                    to enable online consults.
                  </p>
                )}
            </div>
          </div>

          <hr className="create-class-modal-separator" />

          <div className="create-class-modal-cols">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">
                Availability &amp; coaches
              </h3>
              <p className="create-class-modal-section-desc">
                Pick a coach per time range, or leave it unassigned - bookings
                in an unassigned range are flagged so you can assign someone
                later.
              </p>
            </div>

            <div className="create-class-modal-form create-tour-modal-form">
              <div className="create-class-modal-weekdays-group">
                <span className="create-class-modal-label">Available days</span>
                <fieldset className="create-class-modal-weekdays">
                  <legend className="sr-only">Days of the week</legend>
                  {WEEK_DAYS.map((day) => (
                    <label key={day.key} className="checkbox-input">
                      <input
                        type="checkbox"
                        checked={(dayRanges[day.key] ?? []).length > 0}
                        onChange={() => toggleDay(day.key)}
                      />
                      <span className="checkbox" aria-hidden="true" />
                      <span className="text">{day.short}</span>
                    </label>
                  ))}
                </fieldset>
              </div>

              <ul className="scheduling-tour-days">
                {WEEK_DAYS.filter(
                  (day) => (dayRanges[day.key] ?? []).length > 0
                ).map((day) => {
                  const rows = dayRanges[day.key] ?? [];
                  return (
                    <li key={day.key} className="scheduling-tour-day">
                      <div className="scheduling-tour-day-head">
                        <span className="scheduling-tour-day-name">
                          {day.label}
                        </span>
                        <button
                          type="button"
                          data-button-type="tertiary"
                          onClick={() => addRange(day.key)}
                        >
                          + Add range
                        </button>
                      </div>

                      <ul className="scheduling-tour-ranges">
                        {rows.map((row) => (
                          <li key={row.id} className="scheduling-tour-range">
                            <input
                              type="time"
                              aria-label={`${day.label} start time`}
                              value={row.start}
                              onChange={(e) =>
                                patchRange(day.key, row.id, {
                                  start: e.target.value,
                                })
                              }
                            />
                            <span aria-hidden>–</span>
                            <input
                              type="time"
                              aria-label={`${day.label} end time`}
                              value={row.end}
                              onChange={(e) =>
                                patchRange(day.key, row.id, {
                                  end: e.target.value,
                                })
                              }
                            />
                            <div className="scheduling-tour-range-coach">
                              <CoachMultiSelect
                                single
                                emptyLabel="Unassigned"
                                value={row.coachId ? [row.coachId] : []}
                                onChange={(ids) =>
                                  patchRange(day.key, row.id, {
                                    coachId: ids[0] ?? null,
                                  })
                                }
                              />
                            </div>
                            {rows.length > 1 && (
                              <button
                                type="button"
                                data-button-type="tertiary"
                                data-icon-only="true"
                                aria-label={`Remove ${day.label} range`}
                                onClick={() => removeRange(day.key, row.id)}
                              >
                                <img
                                  src="/img/new-icons/red-cross.svg"
                                  alt=""
                                />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>

              {unassignedCount > 0 && (
                <p className="scheduling-tour-unassigned-hint">
                  {unassignedCount === 1
                    ? "1 time range has no coach."
                    : `${unassignedCount} time ranges have no coach.`}{" "}
                  You'll be emailed to assign someone when a guest books one.
                </p>
              )}

              {error && (
                <p className="create-class-modal-error" role="alert">
                  {error}
                </p>
              )}
            </div>
          </div>

          <footer className="scheduling-classes-modal-footer">
            <button
              type="button"
              data-button-type="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              data-button-type="primary"
              onClick={handleSave}
              disabled={submitting || onlineNotConnected}
            >
              {submitting ? "Saving…" : "Save"}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default CreateTourModal;
